import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Alert,
  Box,
  Button,
  CollapsableCard,
  Link,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  AccessForSelectFragment,
  LeaveApprovalRecommendationFragment,
  LeaveEmployeeDataFragment,
  ResourceFieldDataFragment,
  TimeoffCurrentEmployeeDataFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import ExpiredTrialChecker from 'modules/core/components/ExpiredTrialChecker'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { convertHoursToTimeFormat } from 'modules/core/lib/formatTime'
import hasFeature from 'modules/core/lib/hasFeature'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'

import ApprovalsInformationBlock from 'modules/approvals/components/ApprovalsInformationBlock'
import { DelegateApproverSelector } from 'modules/approvals/components/DelegateApproverSelector'
import { useAccessApprovalFlows } from 'modules/approvals/hooks/useAccessApprovalFlows'
import ActionButtons from 'modules/timeoff/components/EditLeaveModal/ActionButtons'
import useForms from 'modules/timeoff/components/EditLeaveModal/useForms'
import useLeaveData, {
  type leaveConfirmationFields as LeaveConfirmationDetails,
} from 'modules/timeoff/components/EditLeaveModal/useLeaveData'
import { useMaterializedApprovalFlowApproverCheck } from 'modules/timeoff/components/EditLeaveModal/useMaterializedApprovalFlowApproverCheck'
import useModalCopies from 'modules/timeoff/components/EditLeaveModal/useModalCopies'
import useUpdateLeave from 'modules/timeoff/components/EditLeaveModal/useUpdateLeave'
import FranceCalendarWarnings from 'modules/timeoff/components/FranceCalendarWarnings'
import LeaveApprovalRecommendation from 'modules/timeoff/components/LeaveApprovalRecommendation'
import { RECOMMENDATION_ACTION } from 'modules/timeoff/components/LeaveApprovalRecommendation/RecommendationAction'
import type { ShiftInfo } from 'modules/timeoff/components/LeaveApprovalRecommendation/getRecommendationDescriptionRich'
import Content from 'modules/timeoff/components/LeaveContent'
import AbsenceSummaryContent from 'modules/timeoff/components/LeaveContent/OtherLeavesForm/DayTypeForm/DayTypes/Days/AbsenceSummary'
import LeaveInfo from 'modules/timeoff/components/LeaveInfo'
import ShiftCoverageModal from 'modules/timeoff/components/ShiftCoverageModal'
import useCustomFields from 'modules/timeoff/hooks/useCustomFields'
import useFetchStats from 'modules/timeoff/hooks/useFetchStats'
import useLabourUnits from 'modules/timeoff/hooks/useLabourUnits'
import { useLeaves } from 'modules/timeoff/lib/LeavesContextProvider'
import allowances from 'modules/timeoff/store/collections/allowances'
import leaveHistories from 'modules/timeoff/store/collections/leaveHistories'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import Allowance from 'modules/timeoff/store/models/Allowance'
import type LeaveHistory from 'modules/timeoff/store/models/LeaveHistory'

import { approveAndRefreshLeave } from './approveAndRefreshLeave'
import EauStatusCard from 'modules/payroll_bundle/pages/IncidenceDetailPage/_LeaveInfo/EauStatusCard'

interface ShiftCoverageInfo {
  shiftsInfo: ShiftInfo[]
  leaveStart: string
  leaveEnd: string
  employeeRequestName: string
  onGoToShifts: () => void
}

interface Props {
  leaveId: string
  onClose: () => void
  onChange?: () => void
  leaveYear?: number
  onReject: () => void
  fromCalendar?: boolean
  fromShifts?: boolean
  visible?: boolean
  delegatedApprover: AccessForSelectFragment | null | undefined
  customResourceFieldsData: ResourceFieldDataFragment[]
  leaveEmployee: LeaveEmployeeDataFragment
  currentEmployee: TimeoffCurrentEmployeeDataFragment
  sickFolderId: string
  leaveCanManageLeaveDocuments: boolean
  recommendation: LeaveApprovalRecommendationFragment | null | undefined
}

const LeaveRecommendationModal = ({
  leaveId,
  onClose,
  onChange,
  leaveYear,
  onReject,
  fromCalendar = false,
  fromShifts = false,
  visible = true,
  delegatedApprover,
  customResourceFieldsData,
  leaveEmployee,
  currentEmployee,
  sickFolderId,
  leaveCanManageLeaveDocuments,
  recommendation,
}: Props) => {
  const [showShiftCoverage, setShowShiftCoverage] = useState(false)
  const [shiftCoverageInfo, setShiftCoverageInfo] = useState<ShiftCoverageInfo | null>(null)
  const shouldClosePortalRef = useRef(true)
  const [hasNewDocuments, setHasNewDocuments] = useState(false)
  const [leaveHistory, setLeaveHistory] = React.useState<LeaveHistory | null>(null)
  const [authorName, setAuthorName] = React.useState<string | null>(null)
  const [authorId, setAuthorId] = React.useState<string | null>(null)
  const [authorType, setAuthorType] = React.useState<'Access' | 'User' | null>(null)
  const [collapsableOpen, setCollapsableOpen] = useState(false)
  const [loadingGoToShifts, setLoadingGoToShifts] = useState(false)

  const { fetchData } = useLeaves()
  const { closePortal } = usePortal()

  useAsyncData(() => {
    if (!fromCalendar) return Promise.resolve()

    return allowances.fetch()
  }, [fromCalendar])

  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const year = useMemo(() => leaveYear || moment().year(), [leaveYear])
  const {
    leave,
    leaveConfirmationDetails,
    loading: leaveDataLoading,
    permissions,
    medicalLeave,
  } = useLeaveData({ leaveId, leaveEmployeeId: leaveEmployee.id })

  const { fetchStats } = useFetchStats()

  const { form, documentForm } = useForms({
    leave,
    effectiveOn: medicalLeave.medicalEffectiveOn,
  })

  const labourUnits = useLabourUnits({
    form,
    employeeTimeoffPolicyId: leaveEmployee.timeoffPolicy?.id,
  })

  const customFields = useCustomFields({
    customResourceFieldsData,
  })

  let latestRecommendationReason = null
  if (recommendation?.action === RECOMMENDATION_ACTION.REVIEW && recommendation?.reason) {
    latestRecommendationReason = recommendation.reason
  }

  const { onSubmit, updateLoading, saveLeaveChanges, saveDocuments } = useUpdateLeave({
    form,
    documentForm,
    leave,
    fetchStats,
    canUpdateLeave: permissions.update,
    getFieldsValues: customFields.getValues,
    onChange,
    leaveEmployeeId: leaveEmployee.id,
    sickFolderId,
  })

  const { title, description } = useModalCopies({
    form,
    leave,
    canApprove: permissions.approve,
    isCurrentEmployee: currentEmployee.id === leaveEmployee.id,
    timeoffManagerFullName: leaveEmployee.timeoffManager?.fullName ?? null,
  })

  const onFetchStats = useCallback(() => {
    fetchStats(leave, leaveEmployee.id)
  }, [leave.id])

  const onDocumentDropped = () => {
    setHasNewDocuments(true)
  }

  const formLeaveId = form.get('leave_type_id').value

  const leaveType = leaveTypes.get(formLeaveId)

  const leaveIdAllowance = leaveType?.getAllowanceForEmployee(leaveEmployee.timeoffPolicy?.id)

  const {
    company: { subscription },
    access,
    canManageTimeoff,
  } = useCurrentSession()

  const { loading: loadingApprovalFlows, approvalFlows } = useAccessApprovalFlows(
    'timeoff',
    access.id
  )

  const {
    loading: isCurrentApproverLoading,
    isCurrentApprover,
    approvalFlowId,
    canApproveFromFlow,
  } = useMaterializedApprovalFlowApproverCheck({
    resourceId: leave.id,
    resourceType: 'Timeoff::Leave',
  })

  const isLoading =
    leaveDataLoading || updateLoading || isCurrentApproverLoading || loadingApprovalFlows

  const timeoffSupervisor =
    canManageTimeoff ||
    (leaveEmployee.timeoffSupervisedByCurrent ?? false) ||
    (leaveEmployee.supervisedByCurrent ?? false)

  const labourUnitsHours = labourUnits.label === Allowance.ALLOWANCE_TYPES.hours
  const hoursText = convertHoursToTimeFormat(labourUnits.value, true)
  const daysText = i18n.tp('time_off.days_with_years', {
    count: labourUnits.value,
  })
  const timeTaken = labourUnitsHours ? hoursText : daysText

  useEffect(() => {
    const isreviewTimeOffRequest = permissions.approve

    if (isreviewTimeOffRequest) {
      track(trackingEvents.timeoff.reviewTimeOffRequestStart, {
        from_shifts: fromShifts,
      })
    }
  }, [permissions.approve, fromShifts])

  const isSubmitDisabled = () => {
    const isDetailsRequired = leaveType?.attributes.details_required
    const hasLeaveDescription: string = form.get('description').value
    const halfDay = form.get('half_day').value
    const hourAmount = form.get('hours_amount_in_cents').value

    if (isDetailsRequired) {
      return isLoading || !((form.isDirty || hasNewDocuments) && hasLeaveDescription)
    }

    const absurdStartTime = !form.startTimeHasValue && !halfDay && !!hourAmount

    return isLoading || !(form.isDirty || hasNewDocuments) || absurdStartTime
  }

  useEffect(() => {
    const fetchLeaveHistory = async () => {
      try {
        const fetchLeaveHistories = await leaveHistories.fetch({
          data: { leave_id: leave.id },
        })
        const dataHistory = fetchLeaveHistories ? fetchLeaveHistories[0] : null
        setLeaveHistory(dataHistory)
        setAuthorName(dataHistory.author_name)
        setAuthorId(dataHistory.author_id)
        setAuthorType(dataHistory.author_type)
      } catch (error) {}
    }
    fetchLeaveHistory()
  }, [leave.id])

  // TODO: it's not enough to check the access id since author in leave history is a polymorphic relationship.
  // Need to be fixed after backend exposes the author type (not critical for now)
  const canSeeRequester =
    leaveHistory != null && (canManageTimeoff || authorId !== currentEmployee.access.id)

  const canDelegateApprover =
    !hasFeature('dev_disable_delegate_approval') &&
    (currentEmployee._policies?.approveLeaves.can || approvalFlows.length > 0)

  const reviewTitle = i18n.t('time_off.approval_recommendation.author_timeoff_request_title', {
    authorName: leaveEmployee.firstName,
  })
  const rejectionReason = leave.attributes.reason

  const isReviewerMode = permissions.approve && leave.isPending()

  const calendarEventStartOn = form.startOn?.isValid() ? form.startOn.hour(12).toDate() : undefined
  const calendarEventFinishOn = form.finishOn?.isValid()
    ? form.finishOn.hour(12).toDate()
    : calendarEventStartOn
  const calendarEventFinishOnNatural = labourUnits?.finishOnNatural
    ? moment(labourUnits.finishOnNatural, 'YYYY-MM-DD', true).hour(12).toDate()
    : calendarEventStartOn
  const calculateCalendarEventFinishOn = () => {
    if (!form.finishOn?.isValid() && calendarEventStartOn) {
      return calendarEventStartOn
    }
    return (labourUnits?.isNatural || labourUnits?.countSmRestDays) && labourUnits?.finishOnNatural
      ? calendarEventFinishOnNatural
      : calendarEventFinishOn
  }

  const hideOverlaps = leave.isPending() && isReviewerMode

  const isStartDateEqualFinishDate =
    form.startOn?.isValid() && form.finishOn?.isValid()
      ? form.startOn.isSame(form.finishOn, 'day')
      : false

  const calculatedFinishOn = calculateCalendarEventFinishOn()

  const isPending = leave.isPending()
  const canApprove = permissions.approve
  const isSameAsOwner = authorType === 'Access' && authorId === currentEmployee.access.id

  const handleApprove = async () => {
    const shiftInfo = await detectShiftCoverage()
    if (shiftInfo && Array.isArray(shiftInfo.shiftsInfo) && shiftInfo.shiftsInfo.length > 0) {
      await saveLeaveChanges()
      await saveDocuments()

      await approveAndRefreshLeave({
        action: isCurrentApprover ? 'approve' : 'approve_all',
        rpcMethod: isCurrentApprover ? 'approve' : 'approve_all',
        leave,
        form,
        canUpdate: permissions.update,
        onFetchStats,
        fetchData: (employeeId) => fetchData(employeeId),
        closePortal,
        onClose: () => {},
        fromShifts,
        employeeId: leaveEmployee.id,
      })
      fetchStats(leave, leaveEmployee.id)
      fetchData(leaveEmployee.id)
      if (onChange) onChange()
      setShowShiftCoverage(true)
      setShiftCoverageInfo(shiftInfo)
      shouldClosePortalRef.current = false
      return
    }
    shouldClosePortalRef.current = true
    await onSubmit()
    fetchData(leaveEmployee.id)
    if (onChange) onChange()
    onClose()
  }

  const detectShiftCoverage = async () => {
    const shiftsInfo: ShiftInfo[] = latestRecommendationReason?.shiftsInfo ?? []
    const shiftsLength = shiftsInfo.length
    if (shiftsLength > 0) {
      const goToShifts = () => {
        const firstShift = shiftsInfo[0]
        if (!firstShift) return
        const startDate = new Date(firstShift.startAt)
        const yearShift = startDate.getFullYear()
        const month = startDate.getMonth() + 1
        const day = startDate.getDate()
        const path = navigation.shifts.customViewGroup.fullPath({
          viewType: 'weekly',
          groupType: 'employees',
          year: yearShift,
          month,
          day,
        })
        navigate(path)
      }
      return {
        shiftsInfo,
        leaveStart: leave.attributes.start_on || '',
        leaveEnd: leave.attributes.finish_on || '',
        employeeRequestName: leaveEmployee.firstName,
        onGoToShifts: goToShifts,
      }
    }
    return null
  }

  const handleGoToShifts = async () => {
    setLoadingGoToShifts(true)
    setShowShiftCoverage(false)
    setLoadingGoToShifts(false)
    setTimeout(() => {
      shiftCoverageInfo?.onGoToShifts?.()
    }, 0)
    onClose()
  }

  const isReviewWithShifts =
    isReviewerMode &&
    latestRecommendationReason?.shiftsInfo &&
    latestRecommendationReason.shiftsInfo.length > 0

  if (!visible) return null
  const toDate = (dateString?: string | null | Date) =>
    dateString ? new Date(dateString) : undefined

  const isLeaveConfirmationDetails = (value: unknown): value is LeaveConfirmationDetails => {
    return (
      typeof value === 'object' &&
      value !== null &&
      'status' in value &&
      'confirmed_start_date' in value &&
      'confirmed_end_date' in value &&
      'updated_at' in value
    )
  }

  return (
    <ExpiredTrialChecker subscription={subscription} onClose={onClose}>
      {showShiftCoverage && shiftCoverageInfo ? (
        <ShiftCoverageModal
          visible={showShiftCoverage}
          onClose={() => {
            setShowShiftCoverage(false)
            onClose()
          }}
          shiftsInfo={shiftCoverageInfo.shiftsInfo}
          leaveStart={shiftCoverageInfo.leaveStart}
          leaveEnd={shiftCoverageInfo.leaveEnd}
          employeeRequestName={shiftCoverageInfo.employeeRequestName}
          onGoToShifts={handleGoToShifts}
        />
      ) : (
        <Modal
          illustration={isReviewerMode ? 'time-off' : 'calendar'}
          title={isReviewerMode ? reviewTitle : title}
          description={description}
          onSubmit={isLoading ? undefined : handleApprove}
          onClose={onClose}
          footer={
            !medicalLeave.isMedical &&
            (!permissions.readOnly || isCurrentApprover) && (
              <ActionButtons
                form={form}
                leave={leave}
                onFetchStats={onFetchStats}
                onReject={onReject}
                onChange={(...args) => {
                  if (onChange) {
                    onChange(...args)
                  }
                }}
                canApprove={permissions.approve}
                canUpdate={permissions.update}
                canRemove={permissions.remove}
                isSubmitDisabled={isSubmitDisabled()}
                isManagerOrAdmin={timeoffSupervisor}
                isCurrentApprover={!!isCurrentApprover}
                approvalFlowId={approvalFlowId}
                isLoading={isLoading}
                fromShifts={fromShifts}
                onClose={onClose}
                onlySubmit={isReviewWithShifts ? true : undefined}
                employeeId={leaveEmployee.id}
                recommendationAction={recommendation?.action}
                canApproveFromFlow={canApproveFromFlow}
                hasPendingChanges={form.isDirty || hasNewDocuments}
              />
            )
          }
        >
          {leaveDataLoading || loadingGoToShifts ? (
            <Loading expand />
          ) : (
            <>
              {calendarEventStartOn && calculatedFinishOn && (
                <Box paddingBottom="s6">
                  <AbsenceSummaryContent
                    title={leaveType?.name || ''}
                    description={timeTaken}
                    fromDate={isStartDateEqualFinishDate ? undefined : calendarEventStartOn}
                    toDate={calculatedFinishOn}
                    color="group1"
                  />
                </Box>
              )}
              <Box paddingY="s12">
                <>
                  {isLeaveConfirmationDetails(leaveConfirmationDetails) &&
                    leaveConfirmationDetails.status !== 'failed_request' && (
                      <Box paddingBottom="s16">
                        <EauStatusCard
                          status={leaveConfirmationDetails.status}
                          retryable={false}
                          confirmedStartDate={toDate(leaveConfirmationDetails.confirmed_start_date)}
                          confirmedEndDate={toDate(leaveConfirmationDetails.confirmed_end_date)}
                          updatedAt={toDate(leaveConfirmationDetails.updated_at)}
                        />
                      </Box>
                    )}
                  <CollapsableCard
                    open={collapsableOpen}
                    contentBackground="lighter000"
                    contentPadding="s0"
                    Card={
                      <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
                        <Text.Normal>
                          {i18n.t('time_off.approval_recommendation.absence_details')}
                        </Text.Normal>{' '}
                        <Button
                          onClick={() => setCollapsableOpen((open) => !open)}
                          type="secondary"
                          iconName={collapsableOpen ? 'chevron-up' : 'chevron-down'}
                        />
                      </Box>
                    }
                    Content={
                      <>
                        <Box
                          border={{
                            top: {
                              width: 's1',
                              style: 'solid',
                              color: 'grey400',
                            },
                          }}
                        />
                        <Box padding="s12">
                          <Content
                            leaveTypes={leaveTypes}
                            documentForm={documentForm}
                            form={form}
                            leave={leave}
                            readOnly={permissions.readOnlyFields}
                            year={year}
                            customFieldsForms={customFields.forms}
                            onDocumentDropped={onDocumentDropped}
                            leaveConfirmationDetails={leaveConfirmationDetails}
                            timeTaken={timeTaken}
                            labourUnits={labourUnits}
                            leaveEmployee={leaveEmployee}
                            isReviewOrEditMode
                            leaveCanManageLeaveDocuments={leaveCanManageLeaveDocuments}
                          />
                        </Box>
                      </>
                    }
                  />
                </>
              </Box>
              <ApprovalsInformationBlock
                resourceId={leave.id}
                resourceType="Timeoff::Leave"
                defaultOpen={false}
                showBoxWhenLoading
              />
              <Box gap="s8">
                <Box gap="s8">
                  {leaveIdAllowance && (
                    <FranceCalendarWarnings
                      allowance={leaveIdAllowance}
                      admin={timeoffSupervisor}
                      labourUnits={labourUnits}
                    />
                  )}
                  {canDelegateApprover && (
                    <DelegateApproverSelector
                      category="timeoff"
                      approverAccessId={delegatedApprover?.id}
                      field={form.get('delegated_approver_id')}
                      employeeAccessId={currentEmployee.access.id}
                      startsOn={form.get('start_on').value}
                      finishOn={form.get('finish_on').value}
                    />
                  )}
                  {medicalLeave.isMedical && (
                    <Alert color="warning">
                      <Text.Normal>
                        {i18n.t('time_off.medical_leave.not_editable_notice.description')}
                      </Text.Normal>
                      <Link.Href href={helpCenterUrl('new_request')} target="_blank">
                        {i18n.t('time_off.medical_leave.not_editable_notice.link')}
                      </Link.Href>
                    </Alert>
                  )}
                </Box>
                {rejectionReason && (
                  <Text.Normal size="200" color="grey800" overflowWrap>
                    {i18n.t('time_off.rejection_reason')}: {rejectionReason}
                  </Text.Normal>
                )}
                {canSeeRequester && (
                  <Box paddingTop="s6">
                    <Text.Normal size="300" color="grey800">
                      {i18n.t('time_off.requester_label')}: {authorName}
                    </Text.Normal>
                  </Box>
                )}
              </Box>

              {isPending && canApprove && !isSameAsOwner ? (
                <Box paddingTop="s16">
                  <LeaveApprovalRecommendation
                    employeeId={leaveEmployee.id}
                    employeeName={leaveEmployee.firstName}
                    recommendation={recommendation}
                    workable={leave.leaveType?.isWorkable}
                  />
                </Box>
              ) : (
                // TODO: Fix this! Adding empty component that increase margin.
                <>
                  {!hideOverlaps && (
                    <Box paddingTop="s16">
                      <LeaveInfo form={form} employeeId={leaveEmployee.id} />
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Modal>
      )}
    </ExpiredTrialChecker>
  )
}

export default observer(LeaveRecommendationModal)
